/**
 * Which permission each menu entry's page needs — the ONE place the sidebar
 * (components/app-sidebar.tsx) and the Settings hub (lib/settings-nav.ts,
 * app/settings/page.tsx) read it from.
 *
 * Owner's rule (2026-10-05): a menu entry the role cannot open is HIDDEN.
 *
 * ── How each entry was derived ───────────────────────────────────────────────
 * From the API, not from a guess about the page: the page's first-load GET was
 * traced to its NestJS handler and the `@CheckAbility(action, Subject)` on it
 * copied here. Where the handler checks two grants (Vishing campaigns, reports
 * and opt-outs also need `read:Recipient`) or a page fails without two calls
 * (the SMTP page reads `/delivery-transports` AND `/settings/smtp`), the entry
 * is `allOf`. A few entries are a WRITE by nature — "Create Campaign",
 * "Create training" — and carry the grant their only purpose needs, so a role
 * that cannot create does not get a menu entry that ends in a disabled button.
 *
 * A module's "Getting started" carries the module's main read grant: a guide
 * to a module the role cannot use is noise.
 *
 * Pages no menu links to that exist to write (`/new`, `/[id]/edit`, a setup
 * wizard) are in PAGE_PERMISSIONS below, read only by the route gate: a role
 * that may only read is told the page is not part of its role.
 *
 * An entry NOT listed here is shown to everyone signed in (Home, My Account,
 * Security — pages whose handlers carry no ability check).
 *
 * The subjects are pinned against the API vocabulary
 * (apps/api/src/auth/casl/permission-vocabulary.ts, read from disk) by
 * lib/auth/__tests__/nav-permissions.spec.ts, and every sidebar and settings
 * URL is checked to be either listed or deliberately open.
 */
import { ROUTES } from '@/lib/routes'
import { can, type PermissionRequirement } from '@/lib/auth/permissions'

export type NavRequirement =
  | PermissionRequirement
  | { allOf: readonly PermissionRequirement[] }
  | { anyOf: readonly PermissionRequirement[] }

const read = (subject: string): PermissionRequirement => ({ action: 'read', subject })
const create = (subject: string): PermissionRequirement => ({ action: 'create', subject })
const update = (subject: string): PermissionRequirement => ({ action: 'update', subject })
const manage = (subject: string): PermissionRequirement => ({ action: 'manage', subject })
const allOf = (...requirements: PermissionRequirement[]): NavRequirement => ({ allOf: requirements })
const anyOf = (...requirements: PermissionRequirement[]): NavRequirement => ({ anyOf: requirements })

/**
 * A module's own read grant. A shared page that lives INSIDE a module's menu —
 * its Email Logs (read:EmailLog), Audit Logs (read:AuditLog), Inbox
 * (read:Inbox), AI Studio, delivery settings — also needs it, so a role with
 * the shared grant but no access to the module does not get a lone module
 * menu holding one log page. The Settings-level logs need the shared grant only.
 */
const RECIPIENTS = read('Recipient')
const WORKFLOWS = read('Workflow')
const VISHING = read('Vishing')
const PHISHING = read('PhishingCampaign')
const ANNOUNCEMENTS = read('Announcement')
const LMS = read('Course')
const JIT = read('JitDetectionRule')
const ESA = read('EsaSettings')
const ER = read('ErSettings')
const PHISHTRAINER = read('PhishtrainerUser')
const GAMIFICATION = read('BadgePack')
const POLICIES = read('Policy')
const SURVEYS = read('Survey')

/** Pages whose handlers carry no ability check: open to everyone signed in. */
export const OPEN_NAV_URLS: readonly string[] = [
  '/dashboard',
  ROUTES.settings.hub,
  ROUTES.settings.profile,
  ROUTES.settings.security,
]

export const NAV_PERMISSIONS: Readonly<Record<string, NavRequirement>> = {
  // ── Top level ────────────────────────────────────────────────────────────
  '/recipients/ai-chat': read('AiChat'),
  // The Feedback page is the ticket CONSOLE: every call on it is read:Feedback,
  // which only the company tier holds. Submitting is the floating widget, open to
  // everyone (create:Feedback); a role that may only submit met 403s and a false
  // "No feedback yet" here (browser QA 2026-10-06).
  '/feedback': read('Feedback'),

  // ── Recipients (recipients/*.controller.ts) ──────────────────────────────
  '/recipients/getting-started': read('Recipient'),
  '/recipients': read('Recipient'),
  '/recipients/org-chart': read('Recipient'),
  '/recipients/groups': read('Recipient'),
  '/recipients/pending-actions': read('Recipient'),
  '/recipients/integrations': read('Recipient'),
  '/recipients/ad-syncer': read('Recipient'),
  // A write entry: POST /recipients/import (and /import/analyze) is create:Recipient;
  // the page also loads GET /domains.
  '/recipients/import': allOf(create('Recipient'), read('Domain')),
  '/recipients/imports': read('Recipient'),
  '/recipients/data-quality': read('Recipient'),
  '/recipients/settings/general': read('Recipient'),
  '/recipients/fields': read('Recipient'),
  '/recipients/settings/tags': read('Recipient'),
  '/recipients/suppression-list': read('Recipient'),
  '/recipients/recycle-bin': read('Recipient'),
  // TooltipSettingsController (settings/settings.controller.ts).
  '/recipients/settings/tooltip': allOf(read('Settings'), RECIPIENTS),
  // GET /settings/ai-chat.
  '/recipients/settings/ai-chat': allOf(read('AiChat'), RECIPIENTS),
  // The shared AuditLogsView (GET /audit-logs).
  '/recipients/audit-log': allOf(read('AuditLog'), RECIPIENTS),

  // ── vCRO (vcro/*.controller.ts — every read is read:Recipient) ───────────
  [ROUTES.vcro.gettingStarted]: read('Recipient'),
  [ROUTES.vcro.riskometer]: read('Recipient'),
  [ROUTES.vcro.people]: read('Recipient'),
  [ROUTES.vcro.breakdown]: read('Recipient'),
  [ROUTES.vcro.watchlists]: read('Recipient'),
  [ROUTES.vcro.actions]: read('Recipient'),
  [ROUTES.vcro.signals]: read('Recipient'),
  [ROUTES.vcro.model]: read('Recipient'),
  [ROUTES.vcro.workspaces]: read('Recipient'),
  [ROUTES.vcro.settings]: read('Recipient'),

  // ── Workflows ────────────────────────────────────────────────────────────
  '/workflows/getting-started': read('Workflow'),
  '/workflows': read('Workflow'),
  '/workflows/templates': read('Workflow'),
  '/review-queue': read('Workflow'),
  // The lists are recipient groups (groups.controller.ts) + workflow usage.
  '/workflows/lists': allOf(read('Recipient'), read('Workflow')),
  '/workflows/email-templates': allOf(read('WorkflowEmailTemplate'), WORKFLOWS),
  '/workflows/email-settings': allOf(read('EmailDeliverySettings'), WORKFLOWS),
  '/workflows/email-log': allOf(read('EmailLog'), WORKFLOWS),
  '/workflows/audit-log': allOf(read('AuditLog'), WORKFLOWS),

  // ── Executive Reports ────────────────────────────────────────────────────
  '/reports': read('CompanyReports'),
  '/reports/analytics': read('PhishingReports'),
  '/reports/entities': read('Entity'),
  '/reports/users': read('PhishingReports'),
  '/reports/anomalies': read('PhishingReports'),
  '/reports/l3': read('PhishingReports'),
  '/reports/false-positives': read('PhishingReports'),
  '/reports/risk-bot': read('AiChat'),
  // GET /reports/risk/thresholds (risk-engine.controller.ts).
  '/reports/settings': read('Recipient'),

  // ── Smishing (smishing/**) ───────────────────────────────────────────────
  '/smishing/getting-started': read('Smishing'),
  '/smishing': read('Smishing'),
  '/smishing/campaigns': read('Smishing'),
  '/smishing/templates': read('Smishing'),
  [ROUTES.smishing.reports]: read('Smishing'),
  // GET reports/groups and reports/people also check read:Recipient.
  [ROUTES.smishing.reportsExecutiveSummary]: allOf(read('Smishing'), read('Recipient')),
  [ROUTES.smishing.reportsCampaigns]: read('Smishing'),
  [ROUTES.smishing.reportsFunnel]: read('Smishing'),
  [ROUTES.smishing.reportsChannels]: read('Smishing'),
  [ROUTES.smishing.reportsTemplates]: read('Smishing'),
  [ROUTES.smishing.reportsDepartments]: allOf(read('Smishing'), read('Recipient')),
  [ROUTES.smishing.reportsPeople]: allOf(read('Smishing'), read('Recipient')),
  [ROUTES.smishing.reportsTrends]: read('Smishing'),
  [ROUTES.smishing.reportsDelivery]: read('Smishing'),
  // GET settings/raw + transports.
  '/smishing/settings': read('SmishingSettings'),

  // ── Deepfake Simulation (deepfake/**, settings/deepfake) ─────────────────
  // The module is one subject for building and reading ('Deepfake'; the campaign
  // controllers, media, targets, threads and reports) with the acts that send mail
  // split off like Smishing's: launching (DeepfakeCampaignLaunch) and answering an
  // employee (DeepfakeReply) are buttons inside these pages, gated there. The
  // wizard writes a draft (create:Deepfake). Settings is the tiered policy and has
  // its own subject, so a role that builds campaigns does not reach it. The
  // campaign's own page (/deepfake/campaigns/[id]) answers to /deepfake/campaigns.
  [ROUTES.deepfake.gettingStarted]: read('Deepfake'),
  [ROUTES.deepfake.root]: read('Deepfake'),
  [ROUTES.deepfake.campaigns]: read('Deepfake'),
  [ROUTES.deepfake.newCampaign]: create('Deepfake'),
  [ROUTES.deepfake.replies]: read('Deepfake'),
  [ROUTES.deepfake.videos]: read('Deepfake'),
  [ROUTES.deepfake.scenarios]: read('Deepfake'),
  [ROUTES.deepfake.meetingPages]: read('Deepfake'),
  [ROUTES.deepfake.reports]: read('Deepfake'),
  // The report pages (deepfake-reports.controller.ts). Overview and risk-tiers are read:Deepfake alone;
  // people, departments and repeat-offenders (and the live feed and the CSV) name people or count them by
  // department, so each also checks read:Recipient (READS_PEOPLE), as Smishing's people reports do.
  [ROUTES.deepfake.reportPages.people]: allOf(read('Deepfake'), read('Recipient')),
  [ROUTES.deepfake.reportPages.departments]: allOf(read('Deepfake'), read('Recipient')),
  [ROUTES.deepfake.reportPages.riskTiers]: read('Deepfake'),
  [ROUTES.deepfake.reportPages.repeatOffenders]: allOf(read('Deepfake'), read('Recipient')),
  // Simulation Domains: the simulation-domain setup, shared with Phishing. Its first call, GET
  // /v1/simulation-domains/mail-settings, is read:Phishing_SendingDomain (the API's grant-only door, open
  // to a tenant with Deepfake and not Phishing), so the page needs that read AS WELL as the module's own;
  // an unmapped route would inherit only read:Deepfake from /deepfake and open on a wall of 403s. The
  // writes inside it (add, verify, publish) are gated in the page on create/update of the same subject.
  [ROUTES.deepfake.settingsMail]: allOf(read('Deepfake'), read('Phishing_SendingDomain')),
  // GET /v1/deepfake/settings (the 3-tier policy).
  [ROUTES.deepfake.settings]: read('DeepfakeSettings'),

  // ── Vishing (vishing/**) ─────────────────────────────────────────────────
  [ROUTES.vishing.gettingStarted]: read('Vishing'),
  [ROUTES.vishing.overview]: read('Vishing'),
  [ROUTES.vishing.voices]: read('Vishing'),
  [ROUTES.vishing.scenarios]: read('Vishing'),
  [ROUTES.vishing.lab]: read('Vishing'),
  [ROUTES.vishing.phonePractice]: read('Vishing'),
  // campaign.controller.ts, report/analytics/insights, opt-out: + read:Recipient.
  [ROUTES.vishing.campaigns]: allOf(read('Vishing'), read('Recipient')),
  [ROUTES.vishing.templates]: read('Vishing'),
  [ROUTES.vishing.reports]: allOf(read('Vishing'), read('Recipient')),
  [ROUTES.vishing.reportPages.executiveSummary]: allOf(read('Vishing'), read('Recipient')),
  [ROUTES.vishing.reportPages.trends]: allOf(read('Vishing'), read('Recipient')),
  [ROUTES.vishing.reportPages.effectiveness]: allOf(read('Vishing'), read('Recipient')),
  [ROUTES.vishing.reportPages.objectives]: allOf(read('Vishing'), read('Recipient')),
  [ROUTES.vishing.reportPages.callOperations]: allOf(read('Vishing'), read('Recipient')),
  [ROUTES.vishing.reportPages.repeatOffenders]: allOf(read('Vishing'), read('Recipient')),
  [ROUTES.vishing.reportPages.weakestLinks]: allOf(read('Vishing'), read('Recipient')),
  [ROUTES.vishing.reportPages.strongestLinks]: allOf(read('Vishing'), read('Recipient')),
  [ROUTES.vishing.optOuts]: allOf(read('Vishing'), read('Recipient')),
  // vishing-settings.controller.ts reads on read:Vishing.
  [ROUTES.vishing.settings]: read('Vishing'),
  [ROUTES.vishing.audit]: allOf(read('AuditLog'), VISHING),

  // ── Phishing (phishing/*.controller.ts) ──────────────────────────────────
  '/phishing/getting-started': read('PhishingDashboard'),
  '/phishing': read('PhishingDashboard'),
  '/phishing/campaigns': read('PhishingCampaign'),
  // A write entry: the wizard exists to create a campaign.
  '/phishing/campaigns/new': { action: 'create', subject: 'PhishingCampaign' },
  '/phishing/campaigns/inbox': allOf(read('Inbox'), PHISHING),
  '/phishing/campaigns/archive': read('PhishingCampaign'),
  '/phishing/inbound-replies': read('PhishingInboundReply'),
  '/phishing/scenarios': read('PhishingScenario'),
  '/phishing/ai-studio': allOf(read('AiStudio'), PHISHING),
  '/phishing/library/emails': read('PhishingEmailTemplate'),
  '/phishing/library/phishing-pages': read('PhishingPage'),
  '/phishing/library/landing-pages': read('PhishingLandingPage'),
  '/phishing/library/brand-themes': read('PhishingBrandTheme'),
  // The reports hub reads dashboard/kpi-strip; every report page phishing-reports.
  '/phishing/reports': read('PhishingDashboard'),
  '/phishing/reports/activity': read('PhishingReports'),
  '/phishing/reports/funnel': read('PhishingReports'),
  '/phishing/reports/devices': read('PhishingReports'),
  '/phishing/reports/geography': read('PhishingReports'),
  '/phishing/reports/bot-activity': read('PhishingReports'),
  '/phishing/reports/effectiveness': read('PhishingReports'),
  '/phishing/reports/executive-summary': read('PhishingReports'),
  '/phishing/reports/campaigns-performance': read('PhishingReports'),
  '/phishing/reports/vulnerability-trend': read('PhishingReports'),
  '/phishing/reports/awareness-trend': read('PhishingReports'),
  '/phishing/reports/reporting-trend': read('PhishingReports'),
  '/phishing/reports/weakest-links': read('PhishingReports'),
  '/phishing/reports/strongest-links': read('PhishingReports'),
  '/phishing/reports/top-reporters': read('PhishingReports'),
  '/phishing/reports/repeat-offenders': read('PhishingReports'),
  '/phishing/reports/departments': read('PhishingReports'),
  '/phishing/reports/designations': read('PhishingReports'),
  '/phishing/categories': read('PhishingCategory'),
  '/phishing/settings/tags': read('PhishingTag'),
  // Simulation domains: the one page for them (GET /phishing/mail-settings).
  // Settings links to it too; the retired Settings URL only redirects here.
  [ROUTES.phishing.simulationDomains]: read('Phishing_SendingDomain'),
  '/phishing/email-log': allOf(read('EmailLog'), PHISHING),
  '/phishing/audit-log': read('PhishingAuditLog'),
  '/phishing/bot-management': read('PhishingCampaign'),

  // ── Announcements (announcements/controllers/*) ──────────────────────────
  '/announcements/getting-started': read('Announcement'),
  '/announcements': read('Announcement'),
  '/announcements/campaigns': read('Announcement'),
  '/announcements/campaigns/archive': read('Announcement'),
  '/announcements/inbox': allOf(read('Inbox'), ANNOUNCEMENTS),
  '/announcements/ai-studio': allOf(read('AiStudio'), ANNOUNCEMENTS),
  '/announcements/library': read('AnnouncementEmailTemplate'),
  '/announcements/library/landing-pages': read('AnnouncementLandingPage'),
  '/announcements/categories': read('AnnouncementCategory'),
  // Every report its own menu item; every handler in reports.controller.ts is
  // read:Announcement. /announcements/reports itself redirects to the Executive
  // Summary and takes the module's rule above.
  '/announcements/reports/executive-summary': read('Announcement'),
  '/announcements/reports/campaigns-performance': read('Announcement'),
  '/announcements/reports/vulnerability-trend': read('Announcement'),
  '/announcements/reports/awareness-trend': read('Announcement'),
  '/announcements/reports/compliance-trend': read('Announcement'),
  '/announcements/reports/engagement-trend': read('Announcement'),
  '/announcements/reports/acknowledgment-trend': read('Announcement'),
  '/announcements/reports/weakest-links': read('Announcement'),
  '/announcements/reports/strongest-links': read('Announcement'),
  '/announcements/reports/repeat-offenders': read('Announcement'),
  '/announcements/reports/departments': read('Announcement'),
  '/announcements/reports/designations': read('Announcement'),
  '/announcements/settings': read('AnnouncementTag'),
  '/announcements/settings/tags': read('AnnouncementTag'),
  '/announcements/settings/senders': read('SenderIdentity'),
  '/announcements/settings/delivery': allOf(read('EmailDeliverySettings'), ANNOUNCEMENTS),
  '/announcements/email-log': allOf(read('EmailLog'), ANNOUNCEMENTS),
  '/announcements/audit-log': read('Announcement'),

  // ── Surveys (surveys/*.controller.ts, 2026-10-12) ────────────────────────
  // Every admin read is read:Survey: the rounds, a round's page and its
  // results (/surveys/[id], /surveys/[id]/results) and the template library
  // (/surveys/templates/[id]) take it as their nearest mapped ancestor.
  '/surveys/getting-started': read('Survey'),
  // The Dashboard (the module root since 17 Oct 2026) and All Surveys (the rounds table).
  '/surveys': read('Survey'),
  '/surveys/all': read('Survey'),
  // Reports (17 Oct 2026): each reads GET /surveys/reports/* on read:Survey; every answer
  // figure honours the anonymity floor server-side, whatever the role.
  '/surveys/reports/executive-summary': read('Survey'),
  '/surveys/reports/response-rates': read('Survey'),
  '/surveys/reports/risk-culture-signals': read('Survey'),
  '/surveys/reports/question-trends': read('Survey'),
  '/surveys/reports/verbatims': read('Survey'),
  '/surveys/reports/participation': read('Survey'),
  '/surveys/reports/exports': read('Survey'),
  // A write entry: the wizard exists to send a round (POST /surveys is
  // create:Survey), and its audience step reads people and groups.
  '/surveys/new': allOf(create('Survey'), read('Recipient')),
  '/surveys/templates': read('Survey'),
  // The module's own e-mail and audit logs (2026-10-15) ask for the shared log right AND the
  // module's, as every other module's do (a role with the shared grant but no Surveys must not
  // get a lone Surveys menu holding one log page).
  '/surveys/email-log': allOf(read('EmailLog'), SURVEYS),
  '/surveys/audit-log': allOf(read('AuditLog'), SURVEYS),

  // ── Policies (policies/*.controller.ts; rebuilt 2026-10-15, DECISIONS L7) ─
  // The Dashboard (/policies), All Policies (/policies/library), a policy's page
  // with its in-page tabs, the version compare and Review (the decision buttons
  // are gated inside: create:PolicyApproval) all read on read:Policy; the
  // pages below that carry another right are PAGE_PERMISSIONS / READ_PAGE_PERMISSIONS.
  '/policies/getting-started': read('Policy'),
  '/policies': read('Policy'),
  '/policies/library': read('Policy'),
  // The menu (owner, 17 Oct 2026): Getting started, Dashboard, All Policies, Templates, a Reports group (below),
  // and a Settings group holding Settings, Email Logs and Audit Logs (v3 F1/D15 had the two logs top-level). The
  // "How it works" page (/policies/workflow) is gone from it: Getting started covers it, and the old address only
  // redirects there (its rule is in READ_PAGE_PERMISSIONS).
  // Templates lists the policy e-mails and their on/off from GET /options/emails (read:Policy), and "Edit" is a
  // link into the Email Centre that shows only to a role that may open it; Settings is ONE page (the old Roles
  // page is its "Roles and approvals" section): viewing is read:Policy, saving is update:PolicySettings (the
  // API's can.update gates the controls), and its Auditor access section is gated inside on read:PolicyAuditAccess.
  '/policies/templates': read('Policy'),
  '/policies/settings': read('Policy'),
  // The module's own e-mail and audit logs ask for the shared log right AND the module's.
  '/policies/email-logs': allOf(read('EmailLog'), POLICIES),
  '/policies/audit-logs': allOf(read('AuditLog'), POLICIES),
  // Reports (owner, 17 Oct 2026: every dedicated report its own item, as Phishing,
  // LMS and Announcements). Each reads GET /policies/reports/* on read:Policy; names
  // and e-mails are blank without read:Recipient (the page says so), the counts stay.
  // Audit Packs builds a pack of people's evidence, so it asks for those rights too.
  '/policies/reports/executive-summary': read('Policy'),
  '/policies/reports/compliance-by-policy': read('Policy'),
  '/policies/reports/groups': read('Policy'),
  '/policies/reports/acknowledgment-trend': read('Policy'),
  '/policies/reports/overdue': read('Policy'),
  '/policies/reports/exemptions': read('Policy'),
  '/policies/reports/languages': read('Policy'),
  '/policies/reports/versions': read('Policy'),
  '/policies/reports/reading': read('Policy'),
  '/policies/reports/audit-packs': allOf(POLICIES, read('PolicyEvidence'), RECIPIENTS),

  // ── LMS (courses/*.controller.ts) ────────────────────────────────────────
  '/lms/getting-started': read('LmsStats'),
  '/lms': read('LmsStats'),
  '/lms/live': read('Course'),
  '/lms/courses': read('Course'),
  '/lms/enrollments': read('LmsEnrollment'),
  '/lms/assignments': read('LmsAssignment'),
  '/lms/automations': read('LmsAutomation'),
  '/lms/automations/templates': read('LmsAutomation'),
  '/lms/reports/v2/executive-summary': read('LmsReports'),
  '/lms/reports/v2/learners': read('LmsReports'),
  '/lms/reports/v2/departments': read('LmsReports'),
  '/lms/reports/v2/managers': read('LmsReports'),
  '/lms/reports/v2/courses': read('LmsReports'),
  '/lms/reports/v2/overdue': read('LmsReports'),
  '/lms/reports/v2/in-progress': read('LmsReports'),
  '/lms/reports/v2/completed': read('LmsReports'),
  // GET /lms/reports/failed, like the other v2 reports.
  '/lms/reports/v2/failed': read('LmsReports'),
  '/lms/reports/v2/non-starters': read('LmsReports'),
  '/lms/reports/v2/repeat-failures': read('LmsReports'),
  '/lms/sent-emails': read('LmsSentEmail'),
  '/lms/inbox': allOf(read('Inbox'), LMS),
  '/lms/recycle-bin': read('Course'),
  '/lms/settings': read('LmsSettings'),
  '/lms/email-log': allOf(read('EmailLog'), LMS),
  '/lms/audit-log': read('LmsAuditLog'),
  '/lms/bot-management': read('LmsBotManagement'),

  // ── Content Studio (content-studio/**) ───────────────────────────────────
  '/content-studio/getting-started': read('ContentDeck'),
  // A write entry: it starts a new deck or video.
  '/content-studio/create': anyOf(
    { action: 'create', subject: 'ContentDeck' },
    { action: 'create', subject: 'ContentVideo' },
  ),
  '/content-studio': read('ContentDeck'),
  '/content-studio/videos': read('ContentVideo'),
  '/content-studio/settings': read('ContentDeck'),
  '/content-studio/free-form': read('ContentDeck'),

  // ── JIT Training (jit-training/controllers/*) ────────────────────────────
  '/jit-training/getting-started': read('JitDetectionRule'),
  '/jit-training': read('JitDetectionRule'),
  '/jit-training/detection-rules': read('JitDetectionRule'),
  '/jit-training/coaching-tips': read('JitCoachingTip'),
  '/jit-training/coaching-tips/templates': read('JitNotificationTemplate'),
  '/jit-training/campaigns': read('JitCoachingCampaign'),
  '/jit-training/setup/integrations': read('JitVendorIntegration'),
  '/jit-training/setup/email': allOf(read('EmailDeliverySettings'), JIT),
  '/jit-training/setup/agent-devices': read('JitVendorIntegration'),
  '/jit-training/reports/coaching-activity': read('JitCoachingMoment'),
  '/jit-training/reports/channels': read('JitCoachingMoment'),
  '/jit-training/reports/templates': read('JitCoachingMoment'),
  '/jit-training/reports/cyber-insurance': read('JitCoachingMoment'),
  '/jit-training/manager': read('JitCoachingMoment'),
  '/jit-training/email-log': allOf(read('EmailLog'), JIT),
  '/jit-training/audit-log': read('JitAuditLog'),

  // ── Email Security (esa/*.controller.ts) ─────────────────────────────────
  // The landing is the module overview (it used to redirect to Settings Sets) and the guide sits beside it,
  // as ER's do: both read the reports.
  '/esa/getting-started': read('EsaReports'),
  '/esa': read('EsaReports'),
  '/esa/settings-sets': read('EsaSettingsSet'),
  '/esa/subscriptions': read('EsaSubscription'),
  '/esa/sender-exceptions': read('EsaSubscription'),
  '/esa/trusted-senders': read('EsaTrustedSender'),
  '/esa/logs': read('EsaAuditLog'),
  '/esa/monitor': read('EsaMessage'),
  '/esa/reports': read('EsaReports'),
  '/esa/settings': read('EsaSettings'),
  '/esa/integration': read('EsaSettings'),
  '/esa/audit-log': allOf(read('AuditLog'), ESA),

  // ── Email Remediator (er/*.controller.ts) ────────────────────────────────
  '/er/getting-started': read('ErReports'),
  '/er': read('ErReports'),
  '/er/reported-mails': read('ErReportedMail'),
  '/er/search-remediate': read('ErSearchRemediate'),
  '/er/reports': read('ErReports'),
  '/er/leaderboard': read('ErReports'),
  '/er/logs': read('ErAuditLog'),
  '/er/settings': read('ErSettings'),
  // The integration page reads delivery transports, no ER subject.
  '/er/integration': allOf(read('DeliveryTransport'), ER),
  '/er/settings/catalogs': read('ErSettings'),
  '/er/settings/branding': read('ErSettings'),
  '/er/settings/report-tags': read('ErSettings'),
  // Static (the manifest URL is built in the browser); belongs to ER settings.
  '/er/settings/install-extension': read('ErSettings'),
  '/er/audit-log': allOf(read('AuditLog'), ER),

  // ── PhishTrainer (phishtrainer/**) ───────────────────────────────────────
  '/phishtrainer': read('PhishtrainerUser'),
  '/phishtrainer/users': read('PhishtrainerUser'),
  '/phishtrainer/inbox': read('PhishtrainerUser'),
  '/phishtrainer/leaderboard': read('PhishtrainerUser'),
  '/phishtrainer/programs': read('PhishtrainerUser'),
  '/phishtrainer/reminders': read('PhishtrainerUser'),
  '/phishtrainer/reports': read('PhishtrainerUser'),
  // GET /phishtrainer/reports/baseline (phishtrainer-reports.controller.ts).
  '/phishtrainer/reports/baseline': read('PhishtrainerUser'),
  '/phishtrainer/engagement': read('PhishtrainerUser'),
  '/phishtrainer/library': read('PhishtrainerSafeAttack'),
  // /phishtrainer/attackflipper, a "not available yet" placeholder with no menu
  // entry until it ships, takes the module's rule (/phishtrainer) above.
  // A write entry: every generation is POST /phishtrainer/generate(/chat),
  // create:PhishtrainerSafeAttack (phishtrainer-generate.controller.ts).
  '/phishtrainer/generate': create('PhishtrainerSafeAttack'),
  '/phishtrainer/phishflag': read('PhishtrainerTaxonomy'),
  '/phishtrainer/settings': read('PhishtrainerSetting'),
  '/phishtrainer/email-log': allOf(read('EmailLog'), PHISHTRAINER),
  // Its own endpoint (phishtrainer-audit-log.controller.ts), not AuditLog.
  '/phishtrainer/audit-log': read('PhishtrainerUser'),

  // ── Gamification (gamification/gamification-admin.controller.ts) ─────────
  '/gamification/badges': read('BadgePack'),
  '/gamification/leaderboard': read('PointTransaction'),
  // The list reads on read:Challenge (Editors and Viewers hold it); creating and
  // editing a challenge stay manage:Challenge, gated inside the page.
  '/gamification/challenges': read('Challenge'),
  '/gamification/activity-log': read('PointTransaction'),
  '/gamification/set-points': read('PointsConfig'),
  '/gamification/defaults': read('GamificationDefaults'),
  '/gamification/email-log': allOf(read('EmailLog'), GAMIFICATION),
  '/gamification/audit-log': allOf(read('AuditLog'), GAMIFICATION),

  // ── User Portal (portal-settings, portal-news, team-portal, knowledge) ───
  '/settings/portal/employee/theme': read('PortalTheme'),
  '/settings/portal/employee/customize': read('PortalSetting'),
  '/settings/portal/employee/login-customize': read('PortalSetting'),
  // Lists recipients (read:Recipient) to preview the PORTAL as one of them: a
  // portal page, so it also needs the portal's own grant — without it a role
  // that reads recipients kept a lone "User Portal" menu (QA, 2026-10-05).
  '/settings/portal/employee/view-as': allOf(read('Recipient'), read('PortalSetting')),
  '/settings/portal/manager/view-as': read('TeamPortalDashboard'),
  '/settings/portal/employee/news': read('PortalNews'),
  [ROUTES.knowledge.gettingStarted]: read('PortalSetting'),
  [ROUTES.knowledge.base]: read('PortalSetting'),
  [ROUTES.knowledge.insights]: read('PortalSetting'),
  [ROUTES.knowledge.assistant('employee')]: read('PortalSetting'),
  [ROUTES.knowledge.assistant('manager')]: read('PortalSetting'),

  // ── Settings (lib/settings-nav.ts) ───────────────────────────────────────
  // The page lists the workspace's domains (GET /domains): without read:Domain it
  // said "No domains configured yet" next to two verified ones.
  [ROUTES.settings.workspace]: allOf(read('Workspace'), read('Domain')),
  [ROUTES.settings.team]: read('User'),
  [ROUTES.settings.roles]: read('Role'),
  [ROUTES.settings.auditLogs]: read('AuditLog'),
  [ROUTES.settings.emailLogs]: read('EmailLog'),
  [ROUTES.settings.notifications]: read('NotificationSetting'),
  '/settings/employees/suppression-list': read('Recipient'),
  [ROUTES.settings.mailDeliveryHub]: read('DeliveryTransport'),
  // GET /delivery-transports?kind=SMTP and GET /settings/smtp: either 403 breaks it.
  [ROUTES.settings.mailDeliverySmtp]: allOf(read('DeliveryTransport'), read('Settings')),
  // GET /integrations/microsoft/app-registrations reads on Integration.
  [ROUTES.settings.mailDeliveryMicrosoft]: allOf(read('DeliveryTransport'), read('Integration')),
  [ROUTES.settings.mailDeliveryGoogle]: read('DeliveryTransport'),
  [ROUTES.settings.mailDeliveryDirectDelivery]: read('DeliveryTransport'),
  [ROUTES.settings.mailDeliveryNotificationDomains]: read('Phishing_SendingDomain'),
  [ROUTES.settings.brandedDomains]: read('TrackingDomain'),
  [ROUTES.settings.emailDelivery]: read('EmailDeliverySettings'),
  // email-gate.controller.ts checks NotificationSetting.
  [ROUTES.settings.emailGate]: read('NotificationSetting'),
  [ROUTES.settings.mailDeliveryWhenToSend]: read('EmailDeliverySettings'),
  // GET /recipients/suppression/mirror (read:Recipient) — a delivery POLICY
  // page, so it also needs the delivery settings grant: without it a role that
  // reads recipients kept a lone "Delivery Policies" group (QA, 2026-10-05).
  [ROUTES.settings.mailDeliverySuppressionList]: allOf(read('Recipient'), read('EmailDeliverySettings')),
  [ROUTES.settings.mailDeliveryFailureHandling]: read('EmailDeliverySettings'),
  [ROUTES.settings.mailDeliveryCompliance]: read('EmailDeliverySettings'),
  [ROUTES.settings.brandLogos]: read('BrandLogo'),
  // GET /companies/profile.
  [ROUTES.settings.organization]: read('Company'),
  // The usage card reads on read:AiStudio, which Editors and Viewers hold; the
  // provider list (GET /ai-studio/providers/available, manage:AiStudio) is gated
  // inside the page.
  [ROUTES.settings.aiStudio]: read('AiStudio'),
  [ROUTES.settings.billing]: read('Billing'),
}

/**
 * Pages NO menu links to that exist to WRITE — `/new`, `/create`, `/[id]/edit`
 * and the like (a setup wizard, a configure step, an "add" form). Each carries
 * the grant its save needs, traced to the API handler's `@CheckAbility`, so a
 * role that may only read sees "not part of your role" instead of a form that
 * fails on save. Edit pages need `update`; their "Duplicate & edit" fallback
 * for built-in records (a `create`) is a secondary path, not the page's job.
 *
 * Keys are app-directory paths: `[id]` matches one URL segment, route groups
 * like `(admin)` are left out. A literal segment beats a `[param]`
 * (`/lms/courses/new/edit` is the create page, `/lms/courses/[id]/edit` the
 * edit page). lib/auth/__tests__/nav-permissions.spec.ts walks apps/web/app and
 * fails when a write page is neither here, in a menu, nor exempted with a reason.
 */
export const PAGE_PERMISSIONS: Readonly<Record<string, NavRequirement>> = {
  // ── Phishing (phishing/*.controller.ts) ──────────────────────────────────
  // PATCH /campaigns/:id is update; start/schedule (manage) are gated in the page.
  '/phishing/campaigns/[id]/edit': update('PhishingCampaign'),
  '/phishing/scenarios/new': create('PhishingScenario'),
  '/phishing/scenarios/[id]/edit': update('PhishingScenario'),
  '/phishing/library/emails/new': create('PhishingEmailTemplate'),
  '/phishing/library/emails/[id]/edit': update('PhishingEmailTemplate'),
  '/phishing/library/landing-pages/new': create('PhishingLandingPage'),
  '/phishing/library/landing-pages/[id]/edit': update('PhishingLandingPage'),
  '/phishing/library/phishing-pages/new': create('PhishingPage'),
  '/phishing/library/phishing-pages/[id]/edit': update('PhishingPage'),
  '/phishing/library/brand-themes/new': create('PhishingBrandTheme'),
  '/phishing/library/brand-themes/[id]/edit': update('PhishingBrandTheme'),

  // ── Announcements (announcements/controllers/*) ──────────────────────────
  '/announcements/campaigns/new': create('Announcement'),
  '/announcements/campaigns/[id]/edit': update('Announcement'),
  '/announcements/library/emails/new': create('AnnouncementEmailTemplate'),
  '/announcements/library/emails/[id]/edit': update('AnnouncementEmailTemplate'),
  '/announcements/library/landing-pages/new': create('AnnouncementLandingPage'),
  '/announcements/library/landing-pages/[id]/edit': update('AnnouncementLandingPage'),

  // ── Smishing (smishing-campaign.controller.ts) ──────────────────────────
  '/smishing/campaigns/new': create('Smishing'),
  '/smishing/campaigns/[id]/edit': update('Smishing'),

  // ── Vishing (campaign.controller.ts: every campaign handler also reads Recipient) ──
  '/vishing/campaigns/new': allOf(create('Vishing'), read('Recipient')),
  '/vishing/campaigns/[id]/edit': allOf(update('Vishing'), read('Recipient')),

  // ── Deepfake Simulation (deepfake-campaign.controller.ts) ───────────────
  // The create page (/deepfake/campaigns/new) is a menu page, ruled above. Editing a
  // draft is PATCH /deepfake/campaigns/:id, update:Deepfake; sending it is
  // DeepfakeCampaignLaunch, gated inside the wizard (a role without it saves a draft).
  '/deepfake/campaigns/[id]/edit': update('Deepfake'),

  // ── Surveys (survey-templates.controller.ts) ─────────────────────────────
  // POST /surveys/templates is create:Survey. A template's own page
  // (/surveys/templates/[id]) reads on read:Survey and opens its builder only
  // when the API says canEdit (PUT is update:Survey).
  '/surveys/templates/new': create('Survey'),

  // ── Policies (policy-assignments.controller.ts, policy-versions.controller.ts) ─
  // Assign: choosing who (groups, people) reads recipients, and assigning,
  // reminding and every due-date change are manage:PolicyAssignment.
  // The editor writes the draft (PATCH …/versions/:id is update:Policy); a
  // viewer who may only read is told the page is not part of their role.
  // /policies/new makes an untitled draft (POST /policies/library) and replaces
  // itself with the editor: a write page outside every menu now that "Create
  // Policy" is a button (the old wizard's audience step is gone). It asks for the
  // create AND update:Policy: the page it becomes is the editor (F4, R4b C9).
  '/policies/new': allOf(create('Policy'), update('Policy')),
  '/policies/[id]/assign': allOf(manage('PolicyAssignment'), RECIPIENTS),
  '/policies/[id]/versions/[versionId]/edit': update('Policy'),

  // ── Gamification ─────────────────────────────────────────────────────────
  // Save creates the pack, then each staged badge (POST …/badges is manage:Badge).
  '/gamification/badges/new': allOf(create('BadgePack'), manage('Badge')),

  // ── Content Studio ───────────────────────────────────────────────────────
  // POST /videos (create:ContentVideo), then the generation (create:AiStudio).
  '/content-studio/videos/new': allOf(create('ContentVideo'), create('AiStudio')),
  // POST /video-lessons/publish and /exports: create:ContentVideo.
  '/content-studio/videos/lessons/new': create('ContentVideo'),
  // The AI deck studio: POST /ai/sessions (create:AiStudio), save (create:ContentDeck).
  '/content-studio/studio': allOf(create('ContentDeck'), create('AiStudio')),
  // The Free Form brief: POST free-form/courses (create:ContentDeck), then the
  // blueprint it drafts with AI (POST …/blueprint, AI Studio quota), so it asks
  // for the AI grant as the deck studio above does. The builder, blueprint,
  // preview and export pages under [id] open on the /content-studio/free-form
  // read and lock their own controls without update:ContentDeck.
  '/content-studio/free-form/new': allOf(create('ContentDeck'), create('AiStudio')),

  // ── JIT Training (jit-training.controller.ts) ────────────────────────────
  '/jit-training/campaigns/new': create('JitCoachingCampaign'),
  '/jit-training/campaigns/[id]/edit': update('JitCoachingCampaign'),
  '/jit-training/coaching-tips/new': create('JitCoachingTip'),
  '/jit-training/coaching-tips/templates/new': create('JitNotificationTemplate'),
  '/jit-training/detection-rules/new': create('JitDetectionRule'),
  // The three [id]/edit pages of tips, templates and rules are ALSO the only view
  // of a built-in item, so they open on READ (READ_PAGE_PERMISSIONS below) and
  // lock their own form without update.
  // The vendor setup wizard saves with PATCH /integrations/:vendorKey.
  '/jit-training/setup/integrations/[vendor]': update('JitVendorIntegration'),

  // ── LMS (courses/*.controller.ts) ────────────────────────────────────────
  // A new course is /lms/courses/new/edit: POST /lms/courses is manage:Course.
  '/lms/courses/new/edit': manage('Course'),
  '/lms/courses/[id]/edit': update('Course'),
  '/lms/assignments/new': create('LmsAssignment'),
  '/lms/automations/new': manage('LmsAutomation'),
  '/lms/automations/[id]/edit': update('LmsAutomation'),

  // ── Recipients (recipients/**, integrations.controller.ts) ───────────────
  '/recipients/add': create('Recipient'),
  // POST /recipients/groups; a static group's members are added after it.
  '/recipients/groups/new': create('Recipient'),
  // POST /recipients/suppression(/bulk) is create:Recipient.
  '/recipients/suppression-list/add': create('Recipient'),
  '/recipients/suppression-list/add/bulk': create('Recipient'),
  '/recipients/suppression-list/add/manual': create('Recipient'),
  // Each sync wizard saves PUT …/sync-config (update) then POST …/sync (manage);
  // HFv2 tests the link (manage) and triggers a sync (manage). Google and
  // Microsoft also pick the domain to connect (GET /domains): without read:Domain
  // the picker is empty and the wizard can never finish (browser QA 2026-10-06).
  '/recipients/integrations/google-workspace/configure': allOf(manage('Recipient'), read('Domain')),
  '/recipients/integrations/microsoft-o365/configure': allOf(manage('Recipient'), read('Domain')),
  '/recipients/integrations/okta/configure': manage('Recipient'),
  '/recipients/integrations/ldap/configure': manage('Recipient'),
  '/recipients/integrations/hfv2/configure': manage('Recipient'),
  // SCIM setup creates the connection, mints its token and pauses directory syncs
  // (POST/PUT /v1/identity/scim/*, manage:Recipient).
  '/recipients/integrations/scim/configure': manage('Recipient'),
  // SAML setup saves the provider through PUT /portal-settings (update:PortalSetting)
  // and tests / turns it on through /v1/identity/sso/* (manage:Workspace).
  '/recipients/integrations/saml/configure': allOf(update('PortalSetting'), manage('Workspace')),

  // ── Workflows ────────────────────────────────────────────────────────────
  '/workflows/new': manage('Workflow'),
  '/workflows/email-templates/new': create('WorkflowEmailTemplate'),
  '/workflows/email-templates/[id]/edit': update('WorkflowEmailTemplate'),
  // A list is a recipient group (POST/PUT /recipients/groups) inside the Workflows menu.
  '/workflows/lists/new': allOf(create('Recipient'), WORKFLOWS),
  '/workflows/lists/[id]/edit': allOf(update('Recipient'), WORKFLOWS),

  // ── PhishTrainer (phishtrainer-*.controller.ts) ──────────────────────────
  '/phishtrainer/library/new': create('PhishtrainerSafeAttack'),
  '/phishtrainer/programs/new': create('PhishtrainerSetting'),
  '/phishtrainer/programs/[id]/edit': update('PhishtrainerSetting'),

  // ── Settings ─────────────────────────────────────────────────────────────
  // The wizard creates the domain, or resumes one (?id=) with updates: either write.
  [ROUTES.settings.brandedDomainsSetup]: anyOf(create('TrackingDomain'), update('TrackingDomain')),
  // The old address; it redirects to the wizard above.
  '/settings/delivery-providers/tracking-domains/setup': anyOf(create('TrackingDomain'), update('TrackingDomain')),
}

/**
 * Pages outside every menu that only READ: a hub, a report, a detail or a
 * "view" page reached from a link. They had no home before — NAV_PERMISSIONS is
 * for menu URLs and PAGE_PERMISSIONS for writes — so they had no rule and
 * opened for every role, which then met nothing but 403s and "No data yet"
 * (browser QA of every page as nine roles, 2026-10-06). The guard
 * lib/auth/__tests__/every-page-has-a-rule.guard.spec.ts keeps every page on
 * disk answering to a rule.
 */
export const READ_PAGE_PERMISSIONS: Readonly<Record<string, NavRequirement>> = {
  // Company Overview (the workspace switcher's "All workspaces"): every call is
  // GET /companies/:id/reports/* (read:CompanyReports).
  '/company-dashboard': read('CompanyReports'),
  // The reports hub links four reports, each read:JitCoachingMoment.
  '/jit-training/reports': read('JitCoachingMoment'),
  // GET /jit-training/moments/:id and the tip it coaches.
  '/jit-training/ack/[id]/quiz': allOf(read('JitCoachingMoment'), read('JitCoachingTip')),
  // The view of a built-in tip, template or rule; the editor locks without update.
  '/jit-training/coaching-tips/[id]/edit': read('JitCoachingTip'),
  '/jit-training/coaching-tips/templates/[id]/edit': read('JitNotificationTemplate'),
  '/jit-training/detection-rules/[id]/edit': read('JitDetectionRule'),
  // LMS › Reports › Failed is a sidebar item now: its rule is in NAV_PERMISSIONS.
  // GET /portal-news.
  '/settings/portal/news': read('PortalNews'),
  // GET/PUT /lms/settings/company-defaults; company-tier only in the API.
  '/settings/company/lms-defaults': read('LmsSettings'),
  // The announcements module's company-scope delivery form.
  '/settings/company/announcement-defaults': read('EmailDeliverySettings'),
  // Google / Microsoft directory sync landing pages pick a domain too.
  '/recipients/integrations/google-workspace': allOf(read('Recipient'), read('Domain')),
  '/recipients/integrations/microsoft-o365': allOf(read('Recipient'), read('Domain')),
  // SCIM manage page: GET /v1/identity/scim/connection and /activity need read:Recipient;
  // its actions (rotate, edit, disconnect) are gated on manage:Recipient in the page.
  '/recipients/integrations/scim': read('Recipient'),
  // SAML manage page and the test-complete landing read GET /v1/identity/sso/summary and
  // /test/:id, which need manage:Workspace.
  '/recipients/integrations/saml': manage('Workspace'),
  '/recipients/integrations/saml/test-complete': manage('Workspace'),
  // Policies (TRACKER P14; Policy Management, DECISIONS L2). One person's
  // acknowledgment record holds what the system knows about them — IP address,
  // device, how they were identified, the PDFs — and has its own subject, so
  // read:Policy alone never opens it; the person lists need read:Recipient too.
  // (Exemptions are gone: a person is Removed with a reason instead, under
  // manage:PolicyAssignment.) The segment is the attestation id the API takes
  // (GET /library/:id/people/:attestationId); L7 spelled it [assignmentId].
  '/policies/[id]/recipients/[attestationId]': allOf(read('PolicyEvidence'), RECIPIENTS, POLICIES),
  // Review (the decision buttons are gated inside: create:PolicyApproval) and the
  // version compare only read; written out here so the contract review's table
  // (finding 22) is in one place, as the page's own rule rather than its ancestor's.
  '/policies/[id]/versions/[versionId]/review': POLICIES,
  '/policies/[id]/compare': POLICIES,
  // "As of" lists the people of a past day: GET /library/:id/as-of is read:Policy
  // + read:Recipient (policy-reports.controller.ts), so the page asks for both (F4, R4 L2).
  '/policies/[id]/as-of': allOf(POLICIES, RECIPIENTS),
  // v3 (G6, L10): the internal audit-report builder reads people and their
  // evidence; the page also guards itself with the same three rights. It moved to
  // /policies/reports/audit-packs on 17 Oct 2026 (Policies → Reports, a menu item
  // ruled in NAV_PERMISSIONS); the old address only redirects there and keeps the
  // rule, so no link opens a door the builder then shuts.
  '/policies/reports': allOf(POLICIES, read('PolicyEvidence'), RECIPIENTS),
  // The "How it works" page (titled Workflow) was removed from the menu and the app on 17 Oct 2026, because Getting
  // started covers it. The address redirects to /policies/getting-started and keeps the rule (read:Policy, which
  // its settings read asked for), so an old link or bookmark is not turned away before it is sent on.
  '/policies/workflow': read('Policy'),
}

const DYNAMIC_SEGMENT = /^\[[^\]]+\]$/

/** The PAGE_PERMISSIONS entry whose path matches; a literal segment beats a `[param]`. */
export function pageRequirement(pathname: string): NavRequirement | undefined {
  const path = pathname.split(/[?#]/)[0].replace(/\/+$/, '') || '/'
  const segments = path.split('/').filter(Boolean)
  let best: { requirement: NavRequirement; dynamic: number } | undefined
  for (const [pattern, requirement] of [...Object.entries(PAGE_PERMISSIONS), ...Object.entries(READ_PAGE_PERMISSIONS)]) {
    const parts = pattern.split('/').filter(Boolean)
    if (parts.length !== segments.length) continue
    let dynamic = 0
    const fits = parts.every((part, i) => {
      if (DYNAMIC_SEGMENT.test(part)) {
        dynamic++
        return true
      }
      return part === segments[i]
    })
    if (fits && (!best || dynamic < best.dynamic)) best = { requirement, dynamic }
  }
  return best?.requirement
}

/** The requirement of a menu URL (query and hash ignored), or undefined when it has none. */
export function navRequirement(url: string): NavRequirement | undefined {
  const path = url.split(/[?#]/)[0]
  return Object.prototype.hasOwnProperty.call(NAV_PERMISSIONS, path) ? NAV_PERMISSIONS[path] : undefined
}

/**
 * Module roots whose entry is the grant of the module's DASHBOARD page, not of
 * the module: they gate their own URL only and are skipped as a prefix, so an
 * unmapped page under them is not refused for lacking the dashboard's grant.
 */
export const EXACT_ONLY_NAV_URLS: readonly string[] = ['/phishing', '/lms', '/er', '/esa', '/jit-training']

/**
 * The requirement of ANY page URL, for the route gate: a write page's own
 * entry (PAGE_PERMISSIONS), else the exact menu entry, else the nearest mapped
 * ancestor (`/settings/roles/<id>` → `/settings/roles`). An open URL, or a page
 * under one (`/settings/<anything>`), has none; so does a URL no ancestor maps
 * (`/admin/…`, `/play`, `/`).
 */
export function routeRequirement(pathname: string): NavRequirement | undefined {
  const path = pathname.split(/[?#]/)[0].replace(/\/+$/, '') || '/'
  const page = pageRequirement(path)
  if (page) return page
  const segments = path.split('/').filter(Boolean)
  for (let depth = segments.length; depth > 0; depth--) {
    const prefix = '/' + segments.slice(0, depth).join('/')
    const exact = depth === segments.length
    if (OPEN_NAV_URLS.includes(prefix)) return undefined
    if (!exact && EXACT_ONLY_NAV_URLS.includes(prefix)) continue
    const requirement = navRequirement(prefix)
    if (requirement) return requirement
  }
  return undefined
}

/** Every single requirement inside a NavRequirement (for the vocabulary check). */
export function requirementParts(requirement: NavRequirement): readonly PermissionRequirement[] {
  if ('allOf' in requirement) return requirement.allOf
  if ('anyOf' in requirement) return requirement.anyOf
  return [requirement]
}

/** Does a KNOWN grant list meet the requirement? */
export function navRequirementMet(requirement: NavRequirement, permissions: readonly string[]): boolean {
  if ('allOf' in requirement) return requirement.allOf.every((r) => can(permissions, r.action, r.subject))
  if ('anyOf' in requirement) return requirement.anyOf.some((r) => can(permissions, r.action, r.subject))
  return can(permissions, requirement.action, requirement.subject)
}

/** May a principal with these KNOWN grants open `url`? Unlisted URLs are open. */
export function navEntryAllowed(url: string, permissions: readonly string[]): boolean {
  const requirement = navRequirement(url)
  return requirement === undefined || navRequirementMet(requirement, permissions)
}

type NavNode = { url: string; items?: NavNode[] }

/**
 * The menu tree with every entry the grants do not open removed. A group (an
 * entry with children) stays while at least one child stays, and goes when
 * every child went — its own URL is a section header, not the reason to show
 * it. Pass only a KNOWN grant list: while loading or after an error the caller
 * renders the tree untouched (fail open — never flash-hide).
 */
export function filterNavByPermission<T extends NavNode>(nodes: readonly T[], permissions: readonly string[]): T[] {
  const out: T[] = []
  for (const node of nodes) {
    if (node.items && node.items.length > 0) {
      const items = filterNavByPermission(node.items, permissions)
      if (items.length > 0) out.push({ ...node, items })
      continue
    }
    if (navEntryAllowed(node.url, permissions)) out.push(node)
  }
  return out
}
